import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import T from '../i18n/content.js';
import { useLang, useT } from '../i18n/LangContext.jsx';

import 'swiper/css';
import 'swiper/css/navigation';

export default function MediaCarousel({ items = [], onSlideChange }) {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!items.length) return null;

  // Slide freely whenever there is more than one item (videos or articles).
  const canSlide = items.length > 1;
  const readLabel = lang === 'en' ? 'Read the article →' : '阅 读 全 文 →';

  const handleChange = (swiper) => {
    setActiveIndex(swiper.activeIndex);
    // Pause + reset every video; play the active one if its data says so.
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      v.pause();
      v.currentTime = 0;
      if (i === swiper.activeIndex && items[i]?.autoPlay) v.play().catch(() => {});
    });
    onSlideChange?.(swiper.activeIndex);
  };

  const prevCover = items[activeIndex - 1]?.coverImage || '';
  const nextCover = items[activeIndex + 1]?.coverImage || '';

  return (
    <div className="media-carousel">
      <Swiper
        modules={[Navigation]}
        onSwiper={(s) => (swiperRef.current = s)}
        onSlideChange={handleChange}
        spaceBetween={0}
        slidesPerView={1}
        className="media-swiper"
        allowTouchMove={canSlide}
        simulateTouch={false}
        keyboard={{ enabled: canSlide }}
      >
        {items.map((item, i) => {
          const isArticle = item.type === 'article';
          return (
            <SwiperSlide key={item.id ?? i}>
              {isArticle ? (
                <button
                  type="button"
                  className="media-slide media-slide--article"
                  onClick={() => navigate(`/media/${item.id}`)}
                  style={
                    item.coverImage
                      ? { backgroundImage: `url('${item.coverImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }
                      : undefined
                  }
                >
                  <span className="media-slide__read label">{readLabel}</span>
                </button>
              ) : (
                <div className="media-slide" style={
                  !item.videoUrl && item.coverImage
                    ? { backgroundImage: `url('${item.coverImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : undefined
                }>
                  {item.videoUrl ? (
                    <video
                      ref={(el) => (videoRefs.current[i] = el)}
                      src={item.videoUrl}
                      autoPlay={!!item.autoPlay}
                      muted={!!item.muted}
                      controls
                      className="media-slide__video"
                    />
                  ) : (
                    <div className="media-slide__placeholder">
                      <span className="label">{t(T.insightsSec.videoPending)}</span>
                    </div>
                  )}
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {canSlide && activeIndex > 0 && (
        <button
          type="button"
          className="media-nav media-nav--prev"
          style={{ '--bg': prevCover ? `url('${prevCover}')` : 'none' }}
          onClick={() => swiperRef.current?.slidePrev()}
          aria-label="Previous"
        >
          <span className="media-nav__arrow">←</span>
        </button>
      )}
      {canSlide && activeIndex < items.length - 1 && (
        <button
          type="button"
          className="media-nav media-nav--next"
          style={{ '--bg': nextCover ? `url('${nextCover}')` : 'none' }}
          onClick={() => swiperRef.current?.slideNext()}
          aria-label="Next"
        >
          <span className="media-nav__arrow">→</span>
        </button>
      )}
    </div>
  );
}
